import { useCallback, useEffect, useState } from 'react'
import { supabase } from '../lib/supabase'
import {
  VisitGroupTemplateOption,
  ContractVisitGroup,
} from '../components/VisitGroupEditor'

interface UseVisitGroupTemplatesResult {
  templates: VisitGroupTemplateOption[]
  loading: boolean
  refresh: () => Promise<void>
  saveGroupAsTemplate: (group: ContractVisitGroup) => Promise<void>
}

/**
 * Loads active visit-group templates from Supabase and provides a helper to persist
 * a `ContractVisitGroup` back into the templates library.
 */
export function useVisitGroupTemplates(): UseVisitGroupTemplatesResult {
  const [templates, setTemplates] = useState<VisitGroupTemplateOption[]>([])
  const [loading, setLoading] = useState(false)

  const refresh = useCallback(async () => {
    try {
      setLoading(true)
      const { data, error } = await supabase
        .from('visit_group_templates')
        .select(
          'id, name, description, recurrence_type, recurrence_days, scheduled_start_time, scheduled_end_time, dsny_integration, dsny_pickup_days, applicable_service_types, is_active'
        )
        .eq('is_active', true)
        .order('name', { ascending: true })
      if (error) throw error
      setTemplates((data || []) as VisitGroupTemplateOption[])
    } catch (e) {
      console.error('Failed to load visit-group templates:', e)
      setTemplates([])
    } finally {
      setLoading(false)
    }
  }, [])

  useEffect(() => {
    refresh()
  }, [refresh])

  const saveGroupAsTemplate = useCallback(
    async (group: ContractVisitGroup) => {
      const suggested =
        window.prompt('Save this visit group as a reusable template. Template name:', group.name) ||
        ''
      const name = suggested.trim()
      if (!name) return
      try {
        const { error } = await supabase.from('visit_group_templates').insert({
          name,
          description: `Saved from visit group "${group.name}"`,
          recurrence_type: group.recurrence_type || 'weekly',
          recurrence_days: group.recurrence_days || [],
          scheduled_start_time: group.scheduled_start_time || null,
          scheduled_end_time: group.scheduled_end_time || null,
          dsny_integration: group.dsny_integration || false,
          dsny_pickup_days: group.dsny_pickup_days || [],
          applicable_service_types: [],
          is_active: true,
        })
        if (error) throw error
        alert(`Template "${name}" saved.`)
        await refresh()
      } catch (e: any) {
        console.error('Failed to save group as template:', e)
        alert('Failed to save template: ' + (e?.message || 'Unknown error'))
      }
    },
    [refresh]
  )

  return { templates, loading, refresh, saveGroupAsTemplate }
}
